import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, ArrowLeft, Send, Banknote, Landmark, Download, FileText, Ban, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const CAN_PREPARE = ["ORG_ADMIN", "ACCOUNTS", "HR"];
const CAN_POST = ["ORG_ADMIN", "ACCOUNTS"];
const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthLabel = (d) => new Date(`${d}T00:00:00`).toLocaleDateString("en-IN", { month: "long", year: "numeric" });
const today = () => new Date().toISOString().slice(0, 10);

async function download(url, fallbackName) {
  const res = await api.get(url, { responseType: "blob" });
  const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || fallbackName;
  const missing = res.headers["x-missing-bank-details"];
  const link = document.createElement("a");
  link.href = URL.createObjectURL(res.data);
  link.download = name;
  link.click();
  URL.revokeObjectURL(link.href);
  if (missing) toast.warning(`Bank details missing for: ${missing}`);
}

export default function PayrollRuns() {
  const { user } = useAuth();
  const canPrepare = CAN_PREPARE.includes(user?.role);
  const canPost = CAN_POST.includes(user?.role);
  const { companies } = useCompany() || {};
  const [runs, setRuns] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, pr] = await Promise.all([api.get("/payroll/runs"), api.get("/projects").catch(() => ({ data: [] }))]);
      setRuns(data);
      setProjects(pr.data || []);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const run = runs.find((r) => r.id === selectedId);
  const replace = (updated) => setRuns((all) => all.map((r) => (r.id === updated.id ? updated : r)));

  const act = async (path, body, message) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/payroll/runs/${run.id}/${path}`, body || {});
      replace(data);
      toast.success(message);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (run) {
    const draft = run.status === "DRAFT";
    const companyName = (companies || []).find((c) => c.id === run.company_id)?.name;
    const statutory = run.lines.reduce((s, l) => s + Number(l.pf_employee) + Number(l.pf_employer) + Number(l.esi_employee) + Number(l.esi_employer) + Number(l.tds) + Number(l.professional_tax), 0);
    return (
      <div className="space-y-5" data-testid="payroll-run-detail">
        <Button variant="ghost" size="sm" onClick={() => setSelectedId(null)}><ArrowLeft className="h-4 w-4" /> All payroll runs</Button>
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-semibold text-foreground">Salaries — {monthLabel(run.period_month)}</h1>
            <p className="text-sm text-muted-foreground">{run.number} · {companyName} · {run.lines.length} employees</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <StatusPill value={run.status} />
            {run.statutory_paid_on && <span className="rounded-full bg-emerald-500/10 px-2 py-0.5 text-xs text-emerald-600 dark:text-emerald-400">Statutory deposited {run.statutory_paid_on}</span>}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
          {[["Gross salary", run.total_gross], ["Employer PF / ESI", run.total_employer], ["Deductions", run.total_deductions], ["Net pay (to bank)", run.total_net]].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-border bg-card p-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
              <p className="mt-2 font-mono text-lg font-bold text-foreground">{inr(value)}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2">
          {draft && canPost && (
            <Button disabled={busy} onClick={() => window.confirm(`Post salaries for ${monthLabel(run.period_month)}? This makes the accounting entries and books site staff cost to their projects.`) && act("post", null, "Payroll posted to the accounts")} data-testid="post-payroll-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Approve &amp; Post
            </Button>
          )}
          {draft && canPrepare && (
            <Button variant="outline" disabled={busy} onClick={async () => {
              if (!window.confirm("Delete this draft run?")) return;
              try { await api.delete(`/payroll/runs/${run.id}`); toast.success("Draft deleted"); setSelectedId(null); load(); } catch (err) { toast.error(getErrorMessage(err)); }
            }}><Trash2 className="h-4 w-4 text-rose-400" /> Delete draft</Button>
          )}
          {run.status !== "DRAFT" && run.status !== "CANCELLED" && canPost && (
            <Button variant="outline" onClick={() => download(`/payroll/runs/${run.id}/bank-file`, "salary.csv").catch((e) => toast.error(getErrorMessage(e)))} data-testid="bank-file-button">
              <Download className="h-4 w-4" /> Bank transfer file (CSV)
            </Button>
          )}
          {run.status === "POSTED" && canPost && <DateAction label="Mark salaries paid" icon={Banknote} title="Salaries paid from bank" description={`Records ${inr(run.total_net)} paid from the bank (Dr Salary Payable / Cr Bank).`} onConfirm={(on) => act("pay", { on }, "Salaries marked paid")} testId="pay-payroll-button" />}
          {["POSTED", "PAID"].includes(run.status) && !run.statutory_paid_on && canPost && statutory > 0 && (
            <DateAction label="Record PF / ESI / TDS / PT deposit" icon={Landmark} title="Statutory dues deposited" description={`Records ${inr(statutory)} deposited with the government (PF, ESI, TDS, professional tax).`} onConfirm={(on) => act("deposit-statutory", { on }, "Statutory deposit recorded")} testId="deposit-statutory-button" />
          )}
          {run.status === "POSTED" && !run.statutory_paid_on && canPost && (
            <Button variant="outline" disabled={busy} onClick={() => window.confirm("Cancel this run? Its accounting entries and project costs are reversed.") && act("cancel", null, "Payroll run cancelled")}>
              <Ban className="h-4 w-4 text-rose-400" /> Cancel run
            </Button>
          )}
        </div>
        {draft && <p className="text-xs text-muted-foreground">Draft: change <b>days paid</b> (leave without pay), TDS, professional tax or other deductions (e.g. advance recovery) per employee. Amounts recalculate when you leave the field.</p>}

        <div className="overflow-x-auto rounded-xl border border-border bg-card">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Employee</TableHead><TableHead>Works at</TableHead><TableHead className="text-right">Days paid</TableHead>
              <TableHead className="text-right">Gross</TableHead><TableHead className="text-right">PF</TableHead><TableHead className="text-right">ESI</TableHead>
              <TableHead className="text-right">TDS</TableHead><TableHead className="text-right">PT</TableHead><TableHead className="text-right">Other</TableHead>
              <TableHead className="text-right">Net pay</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {run.lines.map((l) => (
                <PayrollLineRow key={l.id} run={run} line={l} editable={draft && canPrepare} projectCode={projects.find((p) => p.id === l.project_id)?.code} onUpdated={replace} />
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="payroll-runs-page">
      <PageHeader
        title="Payroll"
        description="One run per month: prepare salaries for every active employee, post them to the accounts, pay through the bank and record PF / ESI / TDS deposits."
        action={canPrepare && <NewRunDialog companies={companies} onCreated={(r) => { setRuns((all) => [r, ...all]); setSelectedId(r.id); }} />}
      />
      <div className="rounded-xl border border-border bg-card">
        {!loading && runs.length === 0 ? (
          <EmptyState title="No payroll runs yet" description="Add employees, then create this month's run." testId="payroll-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow>
              <TableHead>Month</TableHead><TableHead>Number</TableHead><TableHead className="text-right">Employees</TableHead>
              <TableHead className="text-right">Gross</TableHead><TableHead className="text-right">Net pay</TableHead><TableHead>Status</TableHead><TableHead />
            </TableRow></TableHeader>
            <TableBody>
              {runs.map((r) => (
                <TableRow key={r.id} className="cursor-pointer" onClick={() => setSelectedId(r.id)} data-testid={`payroll-run-row-${r.number}`}>
                  <TableCell className="font-medium text-foreground">{monthLabel(r.period_month)}</TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{r.number}</TableCell>
                  <TableCell className="text-right">{r.lines.length}</TableCell>
                  <TableCell className="text-right font-mono text-xs">{inr(r.total_gross)}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold">{inr(r.total_net)}</TableCell>
                  <TableCell><StatusPill value={r.status} /></TableCell>
                  <TableCell className="text-right text-xs text-sky-600 dark:text-sky-400">Open →</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function PayrollLineRow({ run, line, editable, projectCode, onUpdated }) {
  const [vals, setVals] = useState({});
  useEffect(() => {
    setVals({ days_paid: String(Number(line.days_paid)), tds: String(line.tds), professional_tax: String(line.professional_tax), other_deduction: String(line.other_deduction) });
  }, [line]);

  const commit = async (field) => {
    if (String(vals[field]) === String(field === "days_paid" ? Number(line[field]) : line[field])) return;
    try {
      const { data } = await api.patch(`/payroll/runs/${run.id}/lines/${line.id}`, { [field]: vals[field] || "0" });
      onUpdated(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
      setVals({ ...vals, [field]: String(line[field]) });
    }
  };
  const cell = (field) => editable
    ? <Input type="number" step="0.01" min="0" className="ml-auto h-8 w-24 text-right" value={vals[field] ?? ""} onChange={(e) => setVals({ ...vals, [field]: e.target.value })} onBlur={() => commit(field)} />
    : <span className="font-mono text-xs">{field === "days_paid" ? `${Number(line.days_paid)} / ${Number(line.days_in_month)}` : inr(line[field])}</span>;

  return (
    <TableRow>
      <TableCell className="text-foreground">{line.employee_name}</TableCell>
      <TableCell className="text-xs text-muted-foreground">{projectCode || "Office"}</TableCell>
      <TableCell className="text-right">{cell("days_paid")}</TableCell>
      <TableCell className="text-right font-mono text-xs">{inr(line.gross)}</TableCell>
      <TableCell className="text-right font-mono text-xs">{inr(line.pf_employee)}</TableCell>
      <TableCell className="text-right font-mono text-xs">{inr(line.esi_employee)}</TableCell>
      <TableCell className="text-right">{cell("tds")}</TableCell>
      <TableCell className="text-right">{cell("professional_tax")}</TableCell>
      <TableCell className="text-right">{cell("other_deduction")}</TableCell>
      <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{inr(line.net_pay)}</TableCell>
      <TableCell className="text-right">
        <Button size="sm" variant="ghost" title="Payslip PDF" onClick={() => download(`/payroll/runs/${run.id}/lines/${line.id}/payslip`, "payslip.pdf").catch((e) => toast.error(getErrorMessage(e)))}>
          <FileText className="h-3.5 w-3.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}

function NewRunDialog({ companies, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", month: new Date().toISOString().slice(0, 7) });
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (open && !form.company_id && companies?.[0]) setForm((f) => ({ ...f, company_id: companies[0].id })); }, [open, companies]);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/payroll/runs", { company_id: form.company_id, period_month: `${form.month}-01` });
      toast.success(`Payroll prepared for ${data.lines.length} employees — review and post`);
      setOpen(false);
      onCreated?.(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="new-payroll-run-button"><Plus className="h-4 w-4" /> New Payroll Run</Button></DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>New payroll run</DialogTitle><DialogDescription>Prepares salaries for every active employee of the company for the month.</DialogDescription></DialogHeader>
        <form onSubmit={create} className="space-y-3.5">
          <div>
            <Label>Company</Label>
            <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
              <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Month</Label><Input required type="month" value={form.month} onChange={(e) => setForm({ ...form, month: e.target.value })} className="mt-1.5" /></div>
          <DialogFooter><Button type="submit" disabled={saving}>{saving && <Loader2 className="h-4 w-4 animate-spin" />} Prepare</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function DateAction({ label, icon: Icon, title, description, onConfirm, testId }) {
  const [open, setOpen] = useState(false);
  const [on, setOn] = useState(today());
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant="outline" data-testid={testId}><Icon className="h-4 w-4" /> {label}</Button></DialogTrigger>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader><DialogTitle>{title}</DialogTitle><DialogDescription>{description}</DialogDescription></DialogHeader>
        <div><Label>Date</Label><Input type="date" value={on} onChange={(e) => setOn(e.target.value)} className="mt-1.5" /></div>
        <DialogFooter><Button onClick={() => { setOpen(false); onConfirm(on); }}>Confirm</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
